// Copyright ©️ 2025 eVolve MEP, LLC

type DurationWithSeconds = `${number}:${number}:${number}`;

/** HH:MM or HH:MM:SS */
export type Duration = `${number}:${number}` | DurationWithSeconds;

export const isDuration = (value: string): value is Duration =>
  /^[1-9]?[0-9]?[0-9]:[0-5][0-9](:[0-5][0-9])?$/.test(value);

export const convertDurationToSeconds = (duration: Duration): number => {
  const isNegative = duration.startsWith('-');
  const [currentHours = 0, currentMinutes = 0, currentSeconds = 0] = duration
    .replace(/^-/, '')
    .split(':')
    .map((v) => parseInt(v, 10));
  return (currentSeconds + currentMinutes * 60 + currentHours * 60 * 60) * (isNegative ? -1 : 1);
};

const formatTime = (n: number) => (n >= 10 ? n : `0${n}`);
export const convertSecondsToDuration = (seconds: number, includeSeconds = false): Duration => {
  const isNegative = seconds < 0;
  let currentSeconds = Math.ceil(Math.abs(seconds));
  let currentMinutes = 0;
  let currentHours = 0;
  currentMinutes += Math.floor(currentSeconds / 60);
  currentSeconds %= 60;
  currentHours += Math.floor(currentMinutes / 60);
  currentMinutes %= 60;

  // If we aren't displaying seconds, round up the seconds to minute (if necessary)
  if (!includeSeconds && currentSeconds >= 30) {
    currentMinutes += 1;
  }
  // If rounding up caused us to hit 60 minutes, move that to the hour
  if (currentMinutes === 60) {
    currentMinutes = 0;
    currentHours += 1;
  }
  const absoluteTime = (
    includeSeconds ? [currentHours, currentMinutes, currentSeconds] : [currentHours, currentMinutes]
  )
    .map(formatTime)
    .join(':');

  return (isNegative ? `-${absoluteTime}` : absoluteTime) as Duration;
};
